/**
 * Protected Route Component
 * Restricts access based on user role
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole: 'admin' | 'instructor' | 'student';
}

/**
 * Wraps routes that require authentication and specific role
 *
 * Examples:
 *   <ProtectedRoute requiredRole="student"><StudentLabs /></ProtectedRoute>
 *   <ProtectedRoute requiredRole="instructor"><ManageClasses /></ProtectedRoute>
 *   <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, loading, hasRole } = useAuth();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // User not authenticated - this shouldn't happen with Authenticator wrapper
  // but handle it gracefully
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // User doesn't have required role
  if (!hasRole(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md p-8 bg-card border rounded-lg text-center">
          <h2 className="text-2xl font-bold mb-4 text-destructive">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-muted-foreground">
            This page requires {requiredRole} role or higher.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Your role: <span className="font-semibold">{user.role}</span>
          </p>
          <button
            onClick={() => window.history.back()}
            className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // User has required role, render children
  return <>{children}</>;
}
